import { TranslationModeEnum } from "@/entities/translation/translation.types"
import { WorkspaceEnum, workspaceOf } from "@/entities/workspace/workspace"
import { useWorkspaceAccess } from "@/entities/workspace/workspace.access"
import {
  Button,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@beringlab/ui"
import { Link, useRouterState } from "@tanstack/react-router"
import { LayoutGrid } from "lucide-react"
import { ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"

/** Each workspace opens on its own front page; a switch never lands inside a record. */
const WorkspaceLink = ({
  workspace,
  isCurrent,
  className,
  onClick,
  children,
}: {
  workspace: WorkspaceEnum
  isCurrent: boolean
  className: string
  onClick: () => void
  children: ReactNode
}) => {
  // `data-current` rather than `aria-current`: Link sets `aria-current` itself from route
  // matching, and overwrites one passed in. A workspace is wider than its front page -- `/orders`
  // is in the translation app -- so the router's answer and this one differ.
  const props = {
    className,
    onClick,
    "data-testid": `workspace-switcher-item-${workspace}`,
    "data-current": isCurrent || undefined,
  }
  switch (workspace) {
    case WorkspaceEnum.Translation:
      return (
        <Link to="/translations" search={{ mode: TranslationModeEnum.Text }} {...props}>
          {children}
        </Link>
      )
    case WorkspaceEnum.Pm:
      return (
        <Link to="/pm" {...props}>
          {children}
        </Link>
      )
    case WorkspaceEnum.BackOffice:
      return (
        <Link to="/admin/user" {...props}>
          {children}
        </Link>
      )
  }
}

/**
 * Moves between the translation app, the PM workspace and the back office.
 *
 * Sits immediately left of the profile button in all three shells, so it is in the same place
 * whichever one is open. Nothing is drawn for an account with a single workspace -- which is every
 * customer -- so the customer header is unchanged.
 */
const WorkspaceSwitcher = ({
  size = "iconLg",
}: {
  size?: "iconSm" | "iconLg"
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const { workspaces } = useWorkspaceAccess()

  if (workspaces.length < 2) return null

  const current = workspaceOf(pathname)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={size}
          aria-label={t("workspace.switch")}
          data-testid="workspace-switcher"
          className={
            isOpen ? "bg-gray-backdrop-300" : "hover:bg-outline-button-hovered"
          }
        >
          <LayoutGrid className="h-5 w-5" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="mr-3 w-[220px] p-1">
        <p className="px-3 pb-1 pt-2 text-xs text-gray-backdrop-900">
          {t("workspace.title")}
        </p>
        <nav className="flex flex-col" aria-label={t("workspace.title")}>
          {workspaces.map((workspace) => {
            const isCurrent = workspace === current
            return (
              <WorkspaceLink
                key={workspace}
                workspace={workspace}
                isCurrent={isCurrent}
                onClick={() => setIsOpen(false)}
                className={cn(
                  "flex items-center justify-between rounded-default px-3 py-2 text-sm",
                  isCurrent
                    ? "bg-key-color/5 font-bold text-key-color"
                    : "text-gray-700 hover:bg-gray-50"
                )}
              >
                <span>{t(`workspace.${workspace}`)}</span>
                {workspace === WorkspaceEnum.Pm && (
                  <span className="rounded-full bg-gray-backdrop-300 px-2 py-0.5 text-[11px] font-medium text-gray-backdrop-900">
                    {t("workspace.inDevelopment")}
                  </span>
                )}
              </WorkspaceLink>
            )
          })}
        </nav>
      </PopoverContent>
    </Popover>
  )
}

export default WorkspaceSwitcher
